import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { AlertTriangle, Calculator } from "lucide-react";

export const Leverage = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Leverage Trading Explained</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Understand how leverage works, its risks, and how to use it effectively</p>
        </div>

        {/* What Is Leverage */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">What Is Leverage?</h2>
          <p className="text-muted-foreground mb-6">
            Leverage allows you to control a larger position than your collateral would normally permit. 
            It's like borrowing money to increase your trading power. On Nordex, you can use leverage 
            from 1x (no leverage) up to 100x depending on market conditions and risk parameters.
          </p>
          
          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Calculator className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">The Formula</h3>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg font-mono text-sm">
              Position Size = Collateral × Leverage
            </div>
          </div>

          <p className="text-muted-foreground mb-4">
            <strong>Example:</strong> If you deposit $1,000 USDC as collateral and use 10x leverage:
          </p>
          <div className="p-4 bg-muted/30 rounded-lg mb-6">
            <p className="text-sm">
              Position Size = $1,000 × 10 = <strong>$10,000</strong>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              You control a $10,000 position with only $1,000 of your own capital.
            </p>
          </div>
        </section>

        {/* How Leverage Works */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">How Leverage Amplifies Returns</h2>
          
          <p className="text-muted-foreground mb-6">
            Leverage amplifies both gains and losses proportionally to the leverage level. Let's see how 
            a 10% price move affects different leverage levels:
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full border border-border rounded-lg">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-4 text-left font-semibold">Leverage</th>
                  <th className="p-4 text-left font-semibold">Collateral</th>
                  <th className="p-4 text-left font-semibold">Position Size</th>
                  <th className="p-4 text-left font-semibold">+10% Move</th>
                  <th className="p-4 text-left font-semibold">-10% Move</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-4 font-medium">1x</td>
                  <td className="p-4 text-sm">$1,000</td>
                  <td className="p-4 text-sm">$1,000</td>
                  <td className="p-4 text-sm text-emerald-500">+$100 (+10%)</td>
                  <td className="p-4 text-sm text-red-500">-$100 (-10%)</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">5x</td>
                  <td className="p-4 text-sm">$1,000</td>
                  <td className="p-4 text-sm">$5,000</td>
                  <td className="p-4 text-sm text-emerald-500">+$500 (+50%)</td>
                  <td className="p-4 text-sm text-red-500">-$500 (-50%)</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">10x</td>
                  <td className="p-4 text-sm">$1,000</td>
                  <td className="p-4 text-sm">$10,000</td>
                  <td className="p-4 text-sm text-emerald-500">+$1,000 (+100%)</td>
                  <td className="p-4 text-sm text-red-500">-$1,000 (-100%)</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">20x</td>
                  <td className="p-4 text-sm">$1,000</td>
                  <td className="p-4 text-sm">$20,000</td>
                  <td className="p-4 text-sm text-emerald-500">+$2,000 (+200%)</td>
                  <td className="p-4 text-sm text-red-500">Liquidated</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Notice at 20x leverage, a -10% price move would result in a -200% loss on your collateral, 
                which means your entire position would be liquidated before reaching -10%.
              </p>
            </div>
          </div>
        </section>

        {/* Leverage Levels */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Choosing Your Leverage Level</h2>
          
          <div className="space-y-4">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Low Leverage (1x-3x)</h3>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">Best for:</strong> Beginners, conservative traders, 
                long-term positions
              </p>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">Risk Level:</strong> Low to Moderate
              </p>
              <p className="text-sm text-muted-foreground">
                Lower leverage gives you more room for price fluctuations without risking liquidation. 
                Your returns are smaller, but so is your risk.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Medium Leverage (5x-10x)</h3>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">Best for:</strong> Intermediate traders, 
                short to medium-term positions
              </p>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">Risk Level:</strong> Moderate to High
              </p>
              <p className="text-sm text-muted-foreground">
                Balances risk and reward. Popular among experienced traders who understand market dynamics 
                and have risk management strategies in place.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">High Leverage (20x-100x)</h3>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">Best for:</strong> Expert traders, scalpers, 
                very short-term positions
              </p>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">Risk Level:</strong> Extreme
              </p>
              <p className="text-sm text-muted-foreground">
                Available only on select markets configured by admin. Extremely risky. Small price movements 
                can result in liquidation. Only recommended for 
                experienced traders with solid risk management and the ability to monitor positions constantly.
              </p>
            </div>
          </div>
        </section>

        {/* Position Sizing */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Position Sizing with Leverage</h2>
          
          <p className="text-muted-foreground mb-6">
            When using leverage, proper position sizing is crucial. Here's a framework to help you 
            determine appropriate position sizes:
          </p>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">1. Determine Your Risk Tolerance</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Decide how much of your total account you're willing to lose on a single trade. 
                Conservative traders use 1-2%, aggressive traders might use 5-10%.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm">
                <strong>Example:</strong> With a $10,000 account and 2% risk tolerance, you're willing 
                to lose $200 on this trade.
              </div>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">2. Calculate Your Stop-Loss Distance</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Determine how far the price can move against you before you exit (or get liquidated). 
                This is your stop-loss distance as a percentage.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm">
                <strong>Example:</strong> You expect to close your position if it moves 5% against you.
              </div>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">3. Calculate Position Size</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Position Size = (Account Size × Risk %) / Stop-Loss %
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm">
                <strong>Example:</strong> ($10,000 × 2%) / 5% = $4,000 position size<br />
                <span className="text-muted-foreground">
                  With 10x leverage, you'd need $400 collateral to open this $4,000 position.
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Margin Modes */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Cross vs Isolated Margin</h2>
          
          <p className="text-muted-foreground mb-6">
            Your margin mode affects how collateral is allocated and how liquidation works:
          </p>

          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Isolated Margin</h3>
              <p className="text-sm text-muted-foreground">
                Each position uses only its allocated margin. If liquidated, only that position's margin 
                is lost -- your other positions and available balance are unaffected.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Cross Margin</h3>
              <p className="text-sm text-muted-foreground">
                All positions share your entire account equity as collateral. This means positions are less 
                likely to be individually liquidated, but a large loss can affect all positions.
              </p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Switch between modes using the "Isolated" / "Cross" selector in the order form. 
            You must close all open positions before switching.
          </p>
        </section>

        {/* Changing Leverage on Open Positions */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Changing Leverage on Open Positions</h2>
          
          <p className="text-muted-foreground mb-6">
            You can adjust leverage on an open position without closing it. In the Positions panel, 
            click the pencil icon next to the leverage value to open the leverage editor.
          </p>

          <p className="text-muted-foreground mb-4">
            The editor shows:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Your current leverage and the new leverage you're selecting</li>
            <li>Current margin allocated to the position</li>
            <li>New margin after the change</li>
            <li>Additional margin required (if increasing leverage) or margin released (if decreasing)</li>
          </ul>
        </section>

        {/* Risks of High Leverage */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Risks of Using High Leverage</h2>
          
          <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
            <li>
              <strong className="text-foreground">Rapid liquidation</strong> -- at 100x leverage, a mere 1% adverse price move results in a 100% loss. 
              Even normal market volatility can wipe out your position in seconds.
            </li>
            <li>
              <strong className="text-foreground">Emotional stress</strong> -- high leverage positions require constant monitoring and can cause significant 
              stress as small price movements result in large P&L swings.
            </li>
            <li>
              <strong className="text-foreground">Overconfidence trap</strong> -- early success with high leverage can lead to overconfidence and taking increasingly 
              risky positions, often resulting in significant losses.
            </li>
            <li>
              <strong className="text-foreground">Flash crashes</strong> -- sudden market moves can liquidate high-leverage positions 
              before you have time to react, even if the price quickly recovers.
            </li>
          </ul>
        </section>

        {/* Best Practices */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Leverage Best Practices</h2>
          
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Start low, scale gradually</strong> -- begin with 1x-2x leverage to understand how it works, then gradually increase as 
              you gain experience and confidence.
            </li>
            <li>
              <strong className="text-foreground">Never risk more than you can afford to lose</strong> -- only use leverage with capital you can afford to lose completely. Never trade with 
              money needed for living expenses or savings.
            </li>
            <li>
              <strong className="text-foreground">Monitor liquidation prices</strong> -- always check your liquidation price before opening a position. Make sure it's far 
              enough away to withstand normal market volatility.
            </li>
            <li>
              <strong className="text-foreground">Use lower leverage for volatile markets</strong> -- during high volatility periods, reduce your leverage to avoid getting stopped out 
              by normal price swings.
            </li>
          </ul>
        </section>

        <DocsNavigation 
          previous={{ title: "Understanding Perpetual Futures", href: "/perpetuals" }}
          next={{ title: "Margin & Liquidation", href: "/margin-liquidation" }}
        />
      </div>
    </DocsLayout>
  );
};

